import { FC, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import useGenerateModelAnswer from '@/api/useGenerateModelAnswer';
import { useQueryClient } from '@tanstack/react-query';
import { extractPdfPageAsImage } from '@/utils/pdfToImage';
import supabaseClient from '@/api/supabaseClient';

interface ModelAnswerButtonProps {
  questionId: string;
  existingModelAnswer?: string | null;
  practiceSetId?: string;
  questionCategory?: string;
  pdfUrl?: string;
  imagePageNumbers?: number[];
}

const ModelAnswerButton: FC<ModelAnswerButtonProps> = ({
  questionId,
  existingModelAnswer,
  practiceSetId,
  questionCategory,
  pdfUrl,
  imagePageNumbers
}) => {
  const [showModal, setShowModal] = useState(false);
  const generateModelAnswer = useGenerateModelAnswer();
  const queryClient = useQueryClient();

  const handleViewModelAnswer = useCallback(async () => {
    setShowModal(true);

    // If we don't have a model answer yet, fetch/generate it
    if (!existingModelAnswer && !generateModelAnswer.isPending) {
      try {
        let imageDataUrl: string | undefined;

        // Check if this is a Section B Image question
        const isSectionBImage = questionCategory === 'SectionB' && imagePageNumbers && imagePageNumbers.length > 0 && pdfUrl;

        if (isSectionBImage) {
          console.log('📸 Section B Image detected - extracting page from PDF');

          // Get public URL for the PDF
          const { data: { publicUrl } } = supabaseClient.storage
            .from('past-papers')
            .getPublicUrl(pdfUrl);

          // Extract the PDF page as an image
          imageDataUrl = await extractPdfPageAsImage(publicUrl, imagePageNumbers[0]);
          console.log('✅ PDF page extracted successfully');
        }

        await generateModelAnswer.mutateAsync({
          questionId,
          imageDataUrl,
        });

        // Invalidate cache to refresh the practice set data with the new model answer
        if (practiceSetId) {
          queryClient.invalidateQueries({ queryKey: ['practice_set', practiceSetId] });
        }
      } catch (error) {
        console.error('Failed to generate model answer:', error);
      }
    }
  }, [questionId, existingModelAnswer, generateModelAnswer, practiceSetId, queryClient, questionCategory, pdfUrl, imagePageNumbers]);

  return (
    <>
      <div className="flex justify-center">
        <button
          onClick={handleViewModelAnswer}
          className="bg-primary hover:bg-brand-hover text-white px-6 py-3 rounded-xl font-medium transition-colors"
        >
          Model answer
        </button>
      </div>

      {/* Model Answer Modal - rendered via portal to escape Swiper transform context */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="font-bold text-xl flex-shrink-0 flex items-center justify-between px-6 py-4 border-b text-gray-900">
              <span>🧠 Model Answer:</span>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <XMarkIcon className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            {/* Body */}
            <div className="overflow-y-auto max-h-[60vh] px-6 py-4">
              <div className="prose max-w-none text-gray-900">
                {generateModelAnswer.isPending ? (
                  <div className="flex items-center gap-3 text-gray-600 py-8">
                    <svg
                      className="animate-spin h-6 w-6 text-orange-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Generating answer prompts...</span>
                  </div>
                ) : generateModelAnswer.isError ? (
                  <p className="text-red-600">Failed to generate answer prompts. Please try again.</p>
                ) : existingModelAnswer ? (
                  <p className="whitespace-pre-wrap break-words text-gray-900">{existingModelAnswer}</p>
                ) : (
                  <p className="text-gray-500">No answer prompts available for this question.</p>
                )}
              </div>
            </div>
            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="bg-accent hover:bg-dark-hover-secondary text-white px-8 py-3 rounded-xl font-medium w-full"
                disabled={generateModelAnswer.isPending}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.getElementById('root')!
      )}
    </>
  );
};

export default ModelAnswerButton;
